// Shared types + helpers for the Correction UI (Task 5).
//
// Two parallel correction surfaces:
//   - Text corrections: rows in the `corrections` table (migration 0001),
//     with the new `status` column from migration 0011 for undo-via-flip.
//     Writes overlay `corrected_text` at render time over the matching
//     verbatim tokens; verbatim payload stays byte-identical.
//   - Span attributions: entries in `transcripts.span_attributions jsonb`
//     (migration 0011). Each entry attributes a span (start_ms / end_ms)
//     to a named speaker, overriding both Soniox's per-token label AND
//     the L1 speaker_map for the covered spans.
//
// Render cascade for speakers (TranscriptView) — 5-tier precedence:
//   1. manual per-turn span  (span_attributions, source='manual')
//   2. manual whole-meeting  (speaker_map — ONLY the "Whole meeting" rename UI;
//                             NOT the retired speaker_mapper output)
//   3. auto per-turn span    (span_attributions, source='auto'/'L3')
//   4. auto label default    (attribution_label_map)
//   5. raw Soniox label / "Not attributed"
// See resolveTurnSpeaker() below for the canonical implementation.
//
// Render cascade for text (TranscriptView):
//   corrections (status != 'dismissed') > raw verbatim tokens
//
// See DECISIONS.md 2026-05-13 "Correction UI" entry for the architectural
// pins.

import { supabase } from "./supabase";
import type { CandidateCorrection } from "./wysiwyg";

// ---------------------------------------------------------------------------
// span_attributions
// ---------------------------------------------------------------------------

export type SpanAttributionSource = "manual" | "auto" | "L3";

export interface SpanAttribution {
  // Client-generated uuid (manual entries) or backend-generated uuid
  // (L3 entries). crypto.randomUUID() on both sides.
  id: string;
  start_ms: number;
  end_ms: number;
  name: string;
  // "manual" = user input via the SpeakerOverride popover.
  // "auto" = produced by the attribution Claude call from the default
  //   cascade (replaces the per-label speaker_mapper as of 2026-05-25).
  // "L3" = read-only legacy from the old opt-in span_attributor pass.
  //   New attribution writes "auto"; older "L3" rows stay readable until
  //   the follow-up deeper-attribution plan ships and consolidates.
  source: SpanAttributionSource;
  // Which Soniox label was overridden at the time the entry was
  // created. Open-the-hood evidence -- the SPA tooltip surfaces this.
  // The render-cascade same-label filter compares this against the
  // turn's raw Soniox label, so spans only apply to same-label turns.
  raw_label_at_creation: string;
  created_at: string;
  created_by: string | null;
  // L3-only fields (manual entries don't carry these). Optional so
  // the TS type matches both shapes without a discriminated union.
  // - confidence: Claude's self-reported 0-1.
  // - reasoning: one short sentence Claude provided for the proposal.
  confidence?: number;
  reasoning?: string;
  // Panel review status (Item 2). Set SPA-side via read-modify-write
  // when the user acts on the entry in the unified ReviewPanel. Absent
  // = "pending" (the default state for fresh L3 entries; manual entries
  // never appear in the panel and never carry status). Render cascade
  // filters out entries with status === "dismissed" (see
  // resolveSpanAttribution below).
  status?: "pending" | "accepted" | "dismissed";
}

// Pure builder for a manual span_attribution entry. Factored out so the
// review-panel "Use <name>" apply path and SpeakerOverride share one shape.
// id / created_at / created_by are injected by the caller so this stays pure +
// unit-testable; callers pass crypto.randomUUID(), new Date().toISOString(),
// and the authed user id.
export function buildManualSpan(input: {
  id: string;
  createdAt: string;
  createdBy: string | null;
  startMs: number;
  endMs: number;
  name: string;
  rawLabel: string;
}): SpanAttribution {
  return {
    id: input.id,
    start_ms: input.startMs,
    end_ms: input.endMs,
    name: input.name,
    source: "manual",
    raw_label_at_creation: input.rawLabel,
    created_at: input.createdAt,
    created_by: input.createdBy,
  };
}

// Append a new span attribution to transcripts.span_attributions for the
// given meeting. Read-modify-write at save time (fresh SELECT, not React
// state) so concurrent writes don't clobber each other -- same discipline
// as SpeakerOverride uses for speaker_map. Optimistic local updates are
// the caller's responsibility; this helper is the synchronous DB write.
export async function appendSpanAttribution(
  meetingId: string,
  entry: SpanAttribution,
): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("transcripts")
    .select("span_attributions")
    .eq("meeting_id", meetingId)
    .maybeSingle();
  if (readErr) throw readErr;
  const current = ((row?.span_attributions ?? []) as SpanAttribution[]).slice();
  current.push(entry);
  const { error: writeErr } = await supabase
    .from("transcripts")
    .update({ span_attributions: current })
    .eq("meeting_id", meetingId);
  if (writeErr) throw writeErr;
}

// Rename an existing span_attribution entry. Used by the inline rename
// affordance on a split-second-half paragraph header (click the speaker
// name -> input -> Enter to save). Read-modify-write keeps the
// concurrency discipline consistent with the other span_attribution
// writers. No status change; just the name field.
export async function renameSpanAttribution(
  meetingId: string,
  id: string,
  newName: string,
): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("transcripts")
    .select("span_attributions")
    .eq("meeting_id", meetingId)
    .maybeSingle();
  if (readErr) throw readErr;
  const current = ((row?.span_attributions ?? []) as SpanAttribution[]).map(
    (e) => (e.id === id ? { ...e, name: newName } : e),
  );
  const { error: writeErr } = await supabase
    .from("transcripts")
    .update({ span_attributions: current })
    .eq("meeting_id", meetingId);
  if (writeErr) throw writeErr;
}

// Set the panel review status on a single span_attribution entry
// (Item 2; ReviewPanel Accept/Dismiss writes). Read-modify-write per the
// existing append/remove discipline. The render cascade filters dismissed
// L3 entries; accepted L3 entries continue to render (the status is a
// record-keeping affordance, not a render-time gate). Manual entries
// don't carry status -- the panel skips them.
export async function setSpanAttributionStatus(
  meetingId: string,
  id: string,
  status: "pending" | "accepted" | "dismissed",
): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("transcripts")
    .select("span_attributions")
    .eq("meeting_id", meetingId)
    .maybeSingle();
  if (readErr) throw readErr;
  const current = ((row?.span_attributions ?? []) as SpanAttribution[]).map(
    (e) => (e.id === id ? { ...e, status } : e),
  );
  const { error: writeErr } = await supabase
    .from("transcripts")
    .update({ span_attributions: current })
    .eq("meeting_id", meetingId);
  if (writeErr) throw writeErr;
}

// Remove a span attribution by id. Used by the inline undo affordance on
// overridden turns. DELETE-on-undo (no status flip) is correct for
// span_attributions -- the entry is pure metadata; the user can recover
// by re-attributing in one click. The asymmetry with corrections (which
// preserve via status='dismissed') is documented in DECISIONS.md
// 2026-05-13 "Correction UI" entry.
export async function removeSpanAttribution(
  meetingId: string,
  id: string,
): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("transcripts")
    .select("span_attributions")
    .eq("meeting_id", meetingId)
    .maybeSingle();
  if (readErr) throw readErr;
  const current = ((row?.span_attributions ?? []) as SpanAttribution[]).filter(
    (e) => e.id !== id,
  );
  const { error: writeErr } = await supabase
    .from("transcripts")
    .update({ span_attributions: current })
    .eq("meeting_id", meetingId);
  if (writeErr) throw writeErr;
}

// Source priority for the render cascade. Higher number wins.
// manual=3 (user ground truth) > auto=2 (attribution Claude call) > L3=1 (legacy opt-in).
function priorityOf(source: SpanAttributionSource): number {
  switch (source) {
    case "manual": return 3;
    case "auto": return 2;
    case "L3": return 1;
  }
}

// Resolve the active span attribution for a given turn.
//
// Cascade rules (DECISIONS.md "Correction UI" entry, with the
// 2026-05-13 same-label render-cascade amendment + 2026-05-14
// source-priority amendment + 2026-05-25 auto-source amendment):
//   1. Time-range match: turn.start_ms must fall within [entry.start_ms,
//      entry.end_ms).
//   2. Label match: entry.raw_label_at_creation must equal the turn's
//      raw Soniox label. The span only applies to turns sharing the
//      label at authorship time -- the same-label walk semantics in
//      the popover are mirrored at render time. WITHOUT this filter,
//      a span saved against spk_3 would wrongly rename intervening
//      spk_1 / spk_2 turns in the same time range.
//   3. Source-priority on overlap: manual > auto > L3, regardless of
//      created_at. Manual entries are user ground truth; auto entries
//      are the new attribution Claude call (replacing speaker_mapper);
//      L3 entries are legacy from the old opt-in span_attributor pass.
//   4. Within the same source, most-recent created_at wins. Older
//      entries become inert but stay in the array (audit trail).
//
// Defensive fallback: entries missing raw_label_at_creation are
// matched on time-range alone. Production entries always set the
// field at save time (SpeakerOverride.saveSpanMode for manual,
// span_attributor/attribution_call for auto/L3), but older or
// externally-inserted rows shouldn't break the cascade.
export function resolveSpanAttribution(
  entries: SpanAttribution[],
  turnStartMs: number,
  turnRawLabel: string,
): SpanAttribution | null {
  let best: SpanAttribution | null = null;
  for (const e of entries) {
    if (turnStartMs < e.start_ms || turnStartMs >= e.end_ms) continue;
    // Same-label filter.
    if (e.raw_label_at_creation && e.raw_label_at_creation !== turnRawLabel) {
      continue;
    }
    // Item 2 status filter: dismissed entries are inert at render time.
    // Manual entries don't carry status; auto/L3 entries Dismissed via
    // the ReviewPanel are filtered out here. Accepted entries render
    // identically to pending (the Accept is a record-keeping affordance,
    // not a render-time gate).
    if (e.status === "dismissed") continue;
    if (!best) {
      best = e;
      continue;
    }
    // Tie-break (3): higher source priority wins unconditionally.
    const ePriority = priorityOf(e.source);
    const bestPriority = priorityOf(best.source);
    if (ePriority > bestPriority) {
      best = e;
      continue;
    }
    if (ePriority < bestPriority) {
      // Keep best (higher priority stays).
      continue;
    }
    // Tie-break (4): same source priority -> most-recent wins.
    if (e.created_at > best.created_at) best = e;
  }
  return best;
}

// ---------------------------------------------------------------------------
// resolveTurnSpeaker — canonical 5-tier name resolver
// ---------------------------------------------------------------------------

// Shape returned by resolveTurnSpeaker. Callers use `source` to decide
// WHICH rendering path to take (SpanOverrideLabel for manual-span,
// speaker-map badge for manual-whole-meeting, etc.).
export type TurnSpeakerSource =
  | "manual-span"       // tier 1: user's per-turn span attribution
  | "manual-label"      // tier 2: user's "Whole meeting" rename (speaker_map)
  | "auto-span"         // tier 3: Claude's per-turn span attribution
  | "auto-label"        // tier 4: Claude's label-level default (attribution_label_map)
  | "none";             // tier 5: no attribution at all

export interface TurnSpeakerResolution {
  name: string | null;
  source: TurnSpeakerSource;
  // The winning SpanAttribution entry (tiers 1 & 3). Null for tier 2/4/5.
  spanEntry: SpanAttribution | null;
}

// Speaker-map entry shape (mirrors the interface in MeetingReview.tsx and
// SpeakerOverride.tsx; duplicated here to keep corrections.ts self-contained).
export interface SpeakerMapEntry {
  name: string | null;
  confidence: number;
  raw_label: string;
}

// Attribution-label-map entry (migration 0025).
export interface LabelMapEntry {
  name: string | null;
  confidence: number;
}

/**
 * Resolve the display name for a turn using the canonical 5-tier precedence.
 *
 * Precedence (manual always beats Claude-auto; per-turn beats whole-meeting):
 *
 *   1. Manual per-turn span   — span_attributions entry covering the turn,
 *                               source === "manual"
 *   2. Manual whole-meeting   — speaker_map[rawLabel].name
 *                               (speaker_map is ONLY the "Whole meeting" rename
 *                               UI write; the retired speaker_mapper.py is no
 *                               longer the writer of this field)
 *   3. Auto per-turn span     — span_attributions entry covering the turn,
 *                               source === "auto" or "L3"
 *   4. Auto label default     — attribution_label_map[rawLabel].name
 *   5. None                   — no attribution; caller shows raw label or pill
 *
 * // TODO: a pre-2025 meeting that was RE-annotated could carry stale auto
 * // speaker_map (from when speaker_mapper.py ran) alongside fresh auto spans.
 * // Under this precedence that stale data would wrongly win at tier 2.
 * // Fix: clear speaker_map on re-annotate.  Affects ~0 current production
 * // meetings (protected benchmark meetings are never re-annotated) so left
 * // for a follow-up migration.
 */
export function resolveTurnSpeaker(
  rawLabel: string,
  spanAttributions: SpanAttribution[],
  speakerMap: Record<string, SpeakerMapEntry>,
  labelMap: Record<string, LabelMapEntry>,
  turnStartMs: number,
): TurnSpeakerResolution {
  const span = resolveSpanAttribution(spanAttributions, turnStartMs, rawLabel);

  // Tier 1: manual per-turn span.
  if (span && span.source === "manual" && span.name) {
    return { name: span.name, source: "manual-span", spanEntry: span };
  }

  // Tier 2: manual whole-meeting rename. speaker_map is keyed by the Soniox
  // label (bare "1" or prefixed "speaker_1" — try both).
  const mapEntry =
    speakerMap[rawLabel] ?? speakerMap[`speaker_${rawLabel}`];
  if (mapEntry?.name) {
    return { name: mapEntry.name, source: "manual-label", spanEntry: null };
  }

  // Tier 3: auto per-turn span (source === "auto" or "L3").
  if (span && span.name) {
    return { name: span.name, source: "auto-span", spanEntry: span };
  }

  // Tier 4: auto label default (attribution_label_map).
  const lmEntry =
    labelMap[rawLabel] ?? labelMap[`speaker_${rawLabel}`];
  if (lmEntry?.name) {
    return { name: lmEntry.name, source: "auto-label", spanEntry: null };
  }

  // Tier 5: no attribution.
  return { name: null, source: "none", spanEntry: null };
}

// ---------------------------------------------------------------------------
// corrections
// ---------------------------------------------------------------------------

export type CorrectionStatus = "accepted" | "dismissed";

export interface Correction {
  id: string;
  meeting_id: string;
  workspace_id: string;
  flag_id: string | null;
  start_ms: number;
  end_ms: number;
  original_text: string | null;
  corrected_text: string;
  promote_to_glossary: boolean;
  status: CorrectionStatus;
  created_at: string;
  // Migration 0020: snapshot of the linked flag's status at the moment
  // the correction was created (flag-link path only; null on free-form
  // and WYSIWYG-pipeline rows where there's no source flag). Used by
  // Undo to restore the flag to its pre-correction state. Nullable.
  pre_correction_status: string | null;
}

// Insert a correction row. Two write paths feed this:
//   (a) Flag-panel Correct button => flag_id populated
//   (b) Free-form click on transcript text => flag_id null
export interface InsertCorrectionInput {
  meeting_id: string;
  workspace_id: string;
  flag_id: string | null;
  start_ms: number;
  end_ms: number;
  original_text: string;
  corrected_text: string;
  promote_to_glossary?: boolean;
}

export async function insertCorrection(
  input: InsertCorrectionInput,
): Promise<Correction> {
  const { data, error } = await supabase
    .from("corrections")
    .insert({
      meeting_id: input.meeting_id,
      workspace_id: input.workspace_id,
      flag_id: input.flag_id,
      start_ms: input.start_ms,
      end_ms: input.end_ms,
      original_text: input.original_text,
      corrected_text: input.corrected_text,
      promote_to_glossary: input.promote_to_glossary ?? false,
    })
    .select(
      "id, meeting_id, workspace_id, flag_id, start_ms, end_ms, original_text, corrected_text, promote_to_glossary, status, created_at, pre_correction_status",
    )
    .single();
  if (error) throw error;
  return data as Correction;
}

// Flip status; used by both dismiss (accepted -> dismissed) and restore
// (dismissed -> accepted) affordances. Optimistic-update is the caller's
// responsibility.
export async function setCorrectionStatus(
  id: string,
  status: CorrectionStatus,
): Promise<void> {
  const { error } = await supabase
    .from("corrections")
    .update({ status })
    .eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// WYSIWYG paragraph-edit RPC
// ---------------------------------------------------------------------------

// Result shape returned by the save_wysiwyg_edit_session RPC (T1.6).
// The RPC wraps a paragraph's worth of candidate corrections into a single
// edit_session meeting_events row + per-correction sub-events; the SPA
// optimistically refreshes the meeting after the call returns.
export interface SaveWysiwygResult {
  created_correction_ids: string[];
  dismissed_correction_ids: string[];
  reverted_flag_ids: string[];
  session_id: string;
  sub_event_ids: string[];
}

// Thin client wrapper around the save_wysiwyg_edit_session RPC. The SPA
// computes a single whole-sub-turn correction from the user's WYSIWYG edit
// (see lib/wysiwyg/savePipeline); this helper attaches the actor and
// the RPC parameters and returns the structured result.
//
// PINNED — auto-retry policy: callers MUST NOT auto-retry on error.
// session_id is the RPC's primary key; a retry with the same session_id
// would PK-collide. If the user wants to retry, they regenerate the
// session at the call site.
export async function saveWysiwygEditSession(params: {
  meeting_id: string;
  workspace_id: string;
  turn_start_ms: number;
  session_id: string;
  started_at: string;
  candidates: CandidateCorrection[];
}): Promise<SaveWysiwygResult> {
  const { data: userData } = await supabase.auth.getUser();
  const actor_user_id = userData.user?.id;
  if (!actor_user_id) throw new Error("not authenticated");

  const { data, error } = await supabase.rpc("save_wysiwyg_edit_session", {
    p_meeting_id: params.meeting_id,
    p_workspace_id: params.workspace_id,
    p_actor_user_id: actor_user_id,
    p_turn_start_ms: params.turn_start_ms,
    p_session_id: params.session_id,
    p_started_at: params.started_at,
    p_candidates: params.candidates,
  });
  if (error) throw error;
  return data as SaveWysiwygResult;
}

// ---------------------------------------------------------------------------
// is-modified predicate (A2 fix)
// ---------------------------------------------------------------------------

// Returns true when a paragraph should display the .is-modified dashed
// accent border — i.e., it carries a USER-originated modification.
//
// Three user-originated modification kinds:
//   1. corrections (always user-originated — every row in the corrections
//      table comes from a user action).
//   2. span_attributions with source = "manual" (user set the speaker via
//      the SpeakerOverride popover).
//   3. paragraph_splits (always user-originated — the user split a turn).
//
// NOT a user modification:
//   - span_attributions with source = "auto" (Claude's default attribution
//     from the annotation cascade). These affect rendering (speaker name
//     is shown) but MUST NOT fire the .is-modified border, which signals
//     "you edited this" to the user.
//   - span_attributions with source = "L3" (legacy review-panel items).
//     L3 entries are Claude-generated flags, not user edits.
//
// Defensive null-source handling: SpanAttributionSource is a non-nullable
// union type, so a missing source should not occur in practice. If it
// somehow does, we do NOT treat it as user-modified (fail-safe: better
// to silently omit the border than to falsely claim the user edited a
// Claude-attributed paragraph).
export function isTurnUserModified(
  hasCorrectionInTurn: boolean,
  spanOverride: SpanAttribution | null,
  isSplitTurn: boolean,
): boolean {
  if (hasCorrectionInTurn) return true;
  if (isSplitTurn) return true;
  if (spanOverride != null && spanOverride.source === "manual") return true;
  return false;
}

// Look up the active correction for a given token. A correction is
// "active" when status === 'accepted'. The token is "matched" when its
// [start_ms, end_ms] falls entirely within [correction.start_ms,
// correction.end_ms]. Returns the matching correction or null.
//
// Multi-correction overlap on the same token: most-recent created_at
// wins. Older overlapping corrections are inert but preserved in the
// DB (audit trail). Same shape as resolveSpanAttribution.
export function resolveCorrectionForToken(
  corrections: Correction[],
  tokenStartMs: number,
  tokenEndMs: number,
): Correction | null {
  let best: Correction | null = null;
  for (const c of corrections) {
    if (c.status !== "accepted") continue;
    if (tokenStartMs >= c.start_ms && tokenEndMs <= c.end_ms) {
      if (!best || c.created_at > best.created_at) best = c;
    }
  }
  return best;
}
